import React, { useRef } from 'react';
import cn from 'classnames';
import { Button, Form, Grid, Input, Select, Text, View, ViewItem } from 'src/components/common';
import './styles.scss';
import { IRootState } from 'src/redux/rootReducer';
import { connect } from 'react-redux';
import { CreateUserPayload, UserInfo, UserModalType } from 'src/redux/users/types';
import { GrClose } from 'react-icons/gr';
import { addUserAsync, updateUserAsync } from 'src/redux/users/actions';
import appConfig from 'src/appConfig';
import _ from 'lodash';
import { getStatusDisplay, LockupValueByLabel } from 'src/utils';
import { ErrorService, Yup } from 'src/services';
import { Formik, FormikProps } from 'formik';
import { CustomModal } from 'src/components/Modal';

export const ModalTypes = {
  VIEW: 'VIEW',
  EDIT: 'EDIT',
  ADD: 'ADD',
};

const UserSchema = Yup.object().shape({
  firstName: Yup.string().required().max(appConfig.TEXT_MEDIUM_LENGTH, ErrorService.MESSAGES.textShortMaxLength),
  lastName: Yup.string().required().max(appConfig.TEXT_MEDIUM_LENGTH, ErrorService.MESSAGES.textShortMaxLength),
  email: Yup.string()
    .email(ErrorService.MESSAGES.invalidEmail)
    .required()
    .max(appConfig.TEXT_SHORT_LENGTH, ErrorService.MESSAGES.emailLength),
  isActive: Yup.boolean().typeError(ErrorService.MESSAGES.required).required(),
  roleId: Yup.number().positive(ErrorService.MESSAGES.required).required(),
});

const UsersModal: React.FC<Props> = ({
  loading,
  data,
  openModalType,
  roles,
  onClose,
  onEdit,
  onUpdateUser,
  onAddUser,
}) => {
  const formRef = useRef<FormikProps<UserInfo>>(null);

  const handleSubmit = (value: UserInfo) => {
    const payload: CreateUserPayload = {
      email: value.email,
      firstName: value.firstName,
      lastName: value.lastName,
      roleId: value.roleId,
      isActive: value.isActive,
    };

    if (openModalType === ModalTypes.EDIT) onUpdateUser({ id: value.id, body: payload });
    if (openModalType === ModalTypes.ADD) onAddUser(payload);
  };

  const renderTitle = () => {
    switch (openModalType) {
      case ModalTypes.VIEW:
        return [
          <View
            key={'close-button'}
            onClick={onClose}
            className={cn('ctn-users-modal__title__button', 'mb-5')}
            isRow
            align={'center'}>
            <GrClose /> <h4>CLOSE</h4>
          </View>,
          <View key={'close-title'} flexGrow={1} justify={'space-between'} isRow align={'center'}>
            <h3>User Details</h3>
            <Button label={'EDIT'} onClick={onEdit} />
          </View>,
        ];
      case ModalTypes.EDIT:
        return <h3>Edit User Details</h3>;
      case ModalTypes.ADD:
        return <h3>Add New User</h3>;
    }
  };
  const statusOptions = [
    { value: true, label: 'Active' },
    { value: false, label: 'Inactive' },
  ];

  const renderBody = modalType => {
    if (modalType === ModalTypes.EDIT || modalType === ModalTypes.ADD) {
      const INITIAL_VALUE: UserInfo =
        modalType === ModalTypes.EDIT
          ? {
              ...data,
              roleId: LockupValueByLabel(data.role, roles) as number,
            }
          : data;
      return (
        <Formik
          key={`user-modal-${modalType}`}
          initialValues={INITIAL_VALUE}
          onSubmit={handleSubmit}
          validationSchema={UserSchema}
          innerRef={formRef}>
          {({ handleSubmit, values, errors, touched, getFieldProps, setFieldValue, setFieldTouched }) => (
            <Form preventDefault autoComplete="off" className="ctn-uam__form">
              <Grid.Wrap>
                <Grid.Item>
                  <Input
                    label="Last Name *"
                    placeholder="Last Name"
                    errorMessage={touched.lastName ? errors.lastName : ''}
                    {...getFieldProps('lastName')}
                  />
                </Grid.Item>
                <Grid.Item>
                  <Input
                    label="First Name *"
                    placeholder="First Name"
                    errorMessage={touched.firstName ? errors.firstName : ''}
                    {...getFieldProps('firstName')}
                  />
                </Grid.Item>
                <Grid.Item>
                  <Input
                    label="Email *"
                    placeholder="Email"
                    errorMessage={touched.email ? errors.email : ''}
                    disabled={openModalType === ModalTypes.EDIT}
                    {...getFieldProps('email')}
                  />
                </Grid.Item>
                <Grid.Item>
                  <Select
                    creatable={false}
                    label="User Role *"
                    name="roleId"
                    placeholder="Choose Here ..."
                    onBlur={setFieldTouched}
                    options={roles}
                    onChange={setFieldValue}
                    value={values.roleId}
                    errorMessage={touched.roleId ? errors.roleId : ''}
                  />
                </Grid.Item>
                <Grid.Item>
                  <Select
                    options={statusOptions}
                    label="Status *"
                    placeholder="Choose Here ..."
                    errorMessage={touched.isActive ? errors.isActive : ''}
                    {...getFieldProps('isActive')}
                    name="isActive"
                    onChange={setFieldValue}
                    onBlur={setFieldTouched}
                    creatable={false}
                  />
                </Grid.Item>
              </Grid.Wrap>
              <View className={cn('ctn-users-modal__footer')}>
                <View flexGrow={1} justify={'flex-end'} isRow className={'mt-32'}>
                  <Button
                    label={'CANCEL'}
                    onClick={onClose}
                    className={cn('mr-5')}
                    variant={'outline'}
                    type={'button'}
                  />
                  <Button type={'button'} onClick={() => handleSubmit()} isLoading={loading}>
                    {openModalType === ModalTypes.ADD ? 'ADD' : 'UPDATE'}
                  </Button>
                </View>
              </View>
            </Form>
          )}
        </Formik>
      );
    }

    return (
      <View className={cn('ctn-users-modal__content mb-16')} flexGrow={1}>
        <Grid.Wrap>
          <ViewItem label={'Last Name'} value={data.lastName} />
          <ViewItem label={'First Name'} value={data.firstName} />
        </Grid.Wrap>
        <Grid.Wrap>
          <ViewItem label={'Email'} value={data.email} />
          <ViewItem label={'User Role'} value={_.capitalize(data.role)} />
        </Grid.Wrap>
        <Grid.Wrap>
          <ViewItem
            label={'Status'}
            value={
              <Text className={cn({ 'ctn-users-modal__status-active': data.isActive })}>
                {getStatusDisplay(data.isActive)}
              </Text>
            }
          />
        </Grid.Wrap>
      </View>
    );
  };

  return (
    <CustomModal
      openModalType={openModalType}
      header={renderTitle()}
      body={renderBody(openModalType)}
      containerClassName="ctn-users-modal__container"
    />
  );
};

type Props = ReturnType<typeof mapStateToProps> &
  typeof mapDispatchToProps & {
    data: UserInfo;
    openModalType: UserModalType;
    onClose?: () => void;
    onEdit?: () => void;
  };

const mapStateToProps = (state: IRootState) => ({
  loading: state.users.isLoading,
  roles: state.content.roles,
  status: state.content.status,
});

const mapDispatchToProps = {
  onUpdateUser: updateUserAsync.request,
  onAddUser: addUserAsync.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(UsersModal);
